/**
 * Act VII — To the Ends of the Earth (Acts – Revelation).
 *
 * A frightened group in an upstairs room becomes a movement across an empire,
 * and the story is given an ending. Four chapters: Pentecost, the persecutor
 * who changed sides, the letters written into real church trouble, and the
 * last two pages of the Bible.
 *
 * Chapter ids and verseIds are stable — progress is persisted against them, so
 * they must not be renamed.
 *
 * Scripture quoted inside scenes is the World English Bible (British Edition),
 * public domain, wrapped in *asterisks* so the renderer can set it in serif.
 */

export const ACT_CHURCH = {
  id: 'church',
  title: 'To the Ends of the Earth',
  summary: 'A frightened group of disciples becomes a movement — and the story is given an ending.',
  span: 'Acts – Revelation',
  chapters: [
    // ── 1 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-pentecost',
      title: 'Wind and Fire',
      where: 'Acts 1–8',
      era: 'c. AD 30',
      artCaption: 'Fire that came apart and rested on each of them',
      scenes: [
        "For forty days after the resurrection he keeps appearing. Then he stops. The last thing they see is a cloud, and two men in white asking why they are still looking at the sky. His final instruction was the least heroic one available: wait. Stay in the city until the power comes. Folded into the promise is a map. *You will be witnesses to me in Jerusalem, in all Judea and Samaria, and to the uttermost parts of the earth.* The book of Acts is that sentence happening, in that order.",

        "About a hundred and twenty of them wait in an upstairs room. They do one piece of business. Judas has to be replaced, and the requirement is narrow. It must be someone who was there the whole time, because the job is to be a witness. Two names go forward. They pray and cast lots. Matthias is chosen and is never mentioned again. Casting lots was ordinary practice all through the Old Testament. This is the last time anyone in the Bible does it. After the next chapter they have something else to go on.",

        "Pentecost is not a Christian invention. The Greek word means fiftieth. It names a Jewish harvest festival, the Feast of Weeks, held fifty days after Passover, one of the three feasts that filled Jerusalem with pilgrims. So the city is at its fullest when the noise starts. A sound from the sky like a violent wind, filling the whole house. Then something like fire, coming apart into separate flames and settling on each person there. Not on the group. On each one.",

        "What the crowd notices is not the fire but the languages. Luke lists where the hearers had come from. Fifteen places, running roughly east to west. It starts with Parthia, outside the Roman empire altogether. Each of them hears the message in the language they grew up speaking. Babel is running backwards. At Babel one language became many and the builders scattered. Here many languages hear one thing, and the hearers gather. Nobody has to give up their own tongue to be included. The message is translated, not imposed.",

        "Some in the crowd decide the disciples are drunk. Peter answers with a fact rather than an argument: it is nine in the morning. Then he stands up and preaches to the crowd that had called for the execution. Seven weeks earlier he could not admit to a servant girl that he knew Jesus. He quotes the prophet Joel on a Spirit poured out on everybody, young and old, men and women, servants included. Then he says the man they crucified is Lord. Three thousand join that day.",

        "The church exists from that afternoon, and it goes wrong almost at once. First money. A couple sell a field, keep part of the price back, and claim they gave all of it. Then food. The Greek-speaking widows are being missed in the daily hand-out while the others are not, and a new office has to be invented to fix it. Then Stephen is stoned, and persecution drives the believers out of Jerusalem. Luke's comment is odd. Those who were scattered went about preaching. What was meant to end the movement spread it.",
      ],
      verseId: 'act-1-8',
      takeaway: 'The church was born by the Spirit, and sent outward from the first day.',
      questions: [
        {
          id: 'sq-pentecost-1',
          question: 'What does the word Pentecost actually mean?',
          options: [
            'Outpouring',
            'Fiftieth',
            'Gathering',
            'Firstfruits',
          ],
          answer: 1,
          explanation:
            'It names the Jewish Feast of Weeks, held fifty days after Passover. The Spirit arrives on a festival that already existed, on the one sort of day when Jerusalem was full of people from everywhere.',
        },
        {
          id: 'sq-pentecost-2',
          question: 'What happens to language when the Spirit arrives?',
          options: [
            'The whole crowd suddenly understands Aramaic',
            'The disciples speak one new heavenly language nobody had heard',
            'Each hearer hears the message in the language they grew up speaking',
            'The apostles preach through interpreters they had trained',
          ],
          answer: 2,
          explanation:
            'The reversal of Babel runs through translation rather than uniformity. Nobody has to give up their own tongue to hear it, which sets the pattern for everything the church does next.',
        },
        {
          id: 'sq-pentecost-3',
          question: 'How did the eleven choose a replacement for Judas?',
          options: [
            'They cast lots — the last time anyone in the Bible does so',
            'Peter appointed him on his own authority',
            'The whole gathering voted by a show of hands',
            'Jesus had named him before the ascension',
          ],
          answer: 0,
          explanation:
            'Lots were standard practice throughout the Old Testament for putting a decision beyond human preference. Acts 1 is the last recorded use; from Pentecost on the church works out its decisions another way.',
        },
        {
          id: 'sq-pentecost-4',
          question: 'What does Luke say the persecution after Stephen’s death produced?',
          options: [
            'It ended the practice of sharing possessions',
            'It persuaded the council to release the apostles',
            'It forced the apostles into hiding for a decade',
            'It scattered the believers, who preached wherever they landed',
          ],
          answer: 3,
          explanation:
            'Acts 8. The word Luke uses for the scattering is the same one used of sowing seed. What was meant to shut the movement down is the mechanism by which it spreads out of Jerusalem.',
        },
      ],
    },

    // ── 2 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-paul',
      title: 'The Persecutor',
      where: 'Acts 9; Romans',
      era: 'c. AD 34–60',
      artCaption: "A road north, and a light that puts a man on the ground",
      scenes: [
        "He is introduced holding coats. Stephen is being stoned outside the city, and the men doing it leave their cloaks with a young man named Saul, who approves of the killing. His background made him valuable. Born in Tarsus, a Roman citizen from birth, trained in Jerusalem under Gamaliel, the most respected teacher of his generation. He is exactly the man you would want on your side. Instead he goes house to house, dragging Christians off to prison.",

        "Damascus is about a hundred and forty miles from Jerusalem, the best part of a week's walk. He sets out with fresh warrants in his bag. What stops him is a light and a voice, and the question is not the one he expects. Not why do you persecute my followers. *Why do you persecute me?* He is blind for three days. The man sent to restore his sight argues with God about it first, then finds him on a street called Straight, which is still there.",

        "The next thirty years are spent travelling. Well over ten thousand miles, on foot and by ship, and he paid his own way much of the time by making tents. He sets out the cost once, reluctantly, in a letter to Corinth. Five floggings of thirty-nine lashes. Three beatings with rods. One stoning that left him for dead. Three shipwrecks. Thirty-nine is not a rough figure. The law of Moses caps a flogging at forty — the rule is in Deuteronomy — so they stopped one short in case somebody lost count.",

        "He did not write his letters, in the sense we mean. He dictated them to a trained secretary, and one of them, Tertius, slips his own greeting into the letter. Paul takes the pen at the end to sign it in his own hand. Romans has shaped Western Christianity more than any other letter, and he wrote it to a church he had never visited. It was carried to Rome, most likely, by a woman named Phoebe. He introduces her as a deacon of the church at Cenchreae.",

        "His central claim comes without conditions. *While we were yet sinners, Christ died for us.* Not once we had improved. Not on the strength of anything we brought. Grace is a gift or it is nothing. That had a practical edge, and it caused the largest structural argument the early church ever had. Did Gentiles have to take on the Jewish law — circumcision, food rules and all — before they could belong? Paul said no, and said it hard enough to confront Peter to his face. The council at Jerusalem agreed with him.",

        "Luke tells the Damascus road story three times, and the versions do not quite line up. In one, the men with Paul hear a voice and see nobody. In another they see the light but cannot make out the words. Luke saw no reason to tidy it up. He gives no ending either. Acts closes with Paul under house arrest in Rome, paying his own rent, still seeing anyone who comes, still preaching. The last word of the book in Greek is a single adverb: unhindered.",
      ],
      verseId: 'rom-5-8',
      takeaway: 'The gospel reached the empire through the man who had tried hardest to stop it.',
      questions: [
        {
          id: 'sq-paul-1',
          question: 'Where does Saul first appear in the story?',
          options: [
            'Preaching in the temple courts',
            'Studying under Gamaliel in Jerusalem',
            'Holding the coats of the men stoning Stephen',
            'Sailing from Tarsus to Jerusalem',
          ],
          answer: 2,
          explanation:
            'Acts 7–8. He is a bystander at the first Christian killing, and Luke adds that he approved of it — which makes the later account of him a deliberate piece of bookkeeping.',
        },
        {
          id: 'sq-paul-2',
          question: 'Paul mentions floggings of thirty-nine lashes. Why thirty-nine rather than forty?',
          options: [
            'Deuteronomy caps a flogging at forty, so the count stopped one short in case it slipped',
            'Roman law capped the flogging of a non-citizen at thirty-nine strokes',
            'Forty was thought an unlucky number, so courts stopped short of it',
            'Jesus had received thirty-nine lashes, and Paul was matching him',
          ],
          answer: 0,
          explanation:
            'The law set a maximum in order to protect the man being beaten from being degraded. Stopping at thirty-nine is a fence built around that limit — a rule made to keep another rule from being broken by accident.',
        },
        {
          id: 'sq-paul-3',
          question: 'Who most likely carried the letter to the Romans to Rome?',
          options: [
            'Timothy, his closest travelling companion',
            'Tertius, the secretary who took it down',
            'Luke, who was collecting material for Acts',
            'Phoebe, a deacon of the church at Cenchreae',
          ],
          answer: 3,
          explanation:
            'Romans 16 commends her to them by name. Letters travelled by hand, and the carrier normally read the thing aloud to the assembly and answered questions about it, which made the choice of courier a serious one.',
        },
        {
          id: 'sq-paul-4',
          question: 'What was the largest structural argument in the early church?',
          options: [
            'Whether the church should move its base from Jerusalem to Antioch',
            'Whether Gentiles had to take on the Jewish law before they could belong',
            'Whether Paul’s letters should be read alongside the scriptures',
            'Whether the Spirit could be given to Samaritans',
          ],
          answer: 1,
          explanation:
            'Settled at the council in Jerusalem in Acts 15, and argued out again in Galatians. Had it gone the other way, Christianity would have remained a movement inside Judaism rather than becoming something anyone could join.',
        },
      ],
    },

    // ── 3 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-letters',
      title: 'Letters to Real Churches',
      where: 'The Epistles',
      era: 'c. AD 48–95',
      artCaption: "Papyrus, a hired scribe, and a letter going out to a real church",
      scenes: [
        "The letters are not essays. They are mail, written into real messes. A church in Corinth suing each other. One in Galatia sliding back into rule-keeping. One in Philippi with two women who had fallen out badly enough to be named in scripture. Twenty-one of the New Testament's twenty-seven books are letters, and the earliest was written about twenty years after the crucifixion — before any of the Gospels. The first Christian writing we have is not an account of Jesus' life. It is a note to an anxious church about members who had died.",

        "An ancient letter opened to a fixed formula: sender, recipient, then a one-word greeting. Paul keeps the shape and changes the word. He puts grace where greetings should be, and adds peace behind it. The Greek hello and the Hebrew one, welded together at the top of nearly everything he wrote. The cost was not trivial. Papyrus was expensive, the secretary was paid for his time, and Romans runs past seven thousand words. These were not dashed off. They were dictated, drafted, copied, then carried by hand for weeks.",

        "Corinth is the clearest window we have into an early congregation, and it is not flattering. They had split into factions named after their favourite preachers. They were taking each other to court. And their communion had gone wrong in a very ordinary way. The meal was a real supper. The well-off arrived early, ate the good food and drank the wine. The labourers could not get away until later, and found the table cleared. Paul's objection is not about ceremony. One of you is hungry, he says, and another is drunk.",

        "The tone varies more than people expect. Paul opens almost every letter by thanking God for its readers. Galatians goes straight from the greeting to *I marvel that you are so quickly deserting him who called you.* No thanksgiving at all. To a reader who knew the form, that is a letter beginning with a slammed door. Elsewhere the advice is short and unglamorous. Faith without works is dead. Be angry, and do not sin. Bear with one another. Christians have argued for centuries over whether James on works fits Paul on faith.",

        "The shortest of Paul's letters is the most awkward. Philemon is a private note about a runaway slave called Onesimus. Paul asks Philemon to take him back not as a slave but as a brother, offers to pay off whatever the man owes, and pointedly refuses to make it an order. We never find out what Philemon did. That is normal. We only have half of these conversations. Paul answers questions from a Corinthian letter we do not have, and mentions letters of his own that nobody has seen since.",

        "Hebrews stands apart. It is anonymous, it reads more like a sermon than a letter, and nobody has ever worked out who wrote it. Tertullian guessed Barnabas. Luther guessed Apollos. Origen wrote in the third century that God alone knows. What the letter does is gather the whole Old Testament and show it pointing forward: better priest, better sacrifice, better promise. Then it names a crowd of people who died without receiving what they were promised, and tells its readers to *run with perseverance the race that is set before us.*",
      ],
      verseId: 'heb-12-1',
      takeaway: 'The New Testament letters are theology written into actual, difficult church life.',
      questions: [
        {
          id: 'sq-letters-1',
          question: 'Which of Paul’s letters opens without any thanksgiving for its readers?',
          options: [
            'Romans',
            'Galatians',
            'Philippians',
            '1 Corinthians',
          ],
          answer: 1,
          explanation:
            'He thanks God for the Corinthians, factions and lawsuits and all. The Galatians get no such opening, which tells you how seriously he took what they were doing before he has made a single argument.',
        },
        {
          id: 'sq-letters-2',
          question: 'What had gone wrong with communion at Corinth?',
          options: [
            'They were using wine that had been offered at a temple',
            'Only the apostles were permitted to serve it',
            'The well-off ate and drank before the poorer members could get there',
            'They had quietly stopped holding it at all',
          ],
          answer: 2,
          explanation:
            'The meal was a real supper, and the ordinary social order of a Roman dinner had simply reasserted itself inside it. Paul treats a seating and timing problem as a theological one.',
        },
        {
          id: 'sq-letters-3',
          question: 'What does Paul ask of Philemon concerning the runaway slave Onesimus?',
          options: [
            'That he take him back not as a slave but as a brother, with Paul covering any debt',
            'That he free him at once and send him back to Paul',
            'That he punish him lightly and restore him to his old duties',
            'That he sell him to Paul at a fair price',
          ],
          answer: 0,
          explanation:
            'Paul has the standing to command it and deliberately does not, asking instead. The letter survives; Philemon’s answer does not, so we have the request without the outcome.',
        },
        {
          id: 'sq-letters-4',
          question: 'Who wrote Hebrews?',
          options: [
            'Paul, writing anonymously to a Jewish readership',
            'Apollos, as the earliest manuscripts record',
            'Barnabas, according to a note inside the letter',
            'Nobody knows — it is anonymous, and the ancient guesses disagree',
          ],
          answer: 3,
          explanation:
            'Origen’s verdict was that God alone knows. The letter was kept because of what it said rather than whose name was on it, which is not how the canon is usually assumed to have worked.',
        },
      ],
    },

    // ── 4 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-new',
      title: 'All Things New',
      where: 'Revelation 21–22',
      era: 'c. AD 95',
      artCaption: "A city coming down, the river and the tree back, its gates never shut",
      scenes: [
        "Revelation was written by a man on Patmos, a rocky island of about thirteen square miles in the Aegean, which Rome used for putting people out of the way. He writes to seven churches on the mainland. Plot them on a map and they fall into a rough circle, in the order a courier would walk them. The book's first word in Greek is the one we get apocalypse from, and it does not mean catastrophe. It means an uncovering. Taking the lid off something, or drawing a veil back from a face.",

        "The messages to those seven churches are startlingly local. Laodicea is told it is neither cold nor hot but lukewarm. Its water came miles by aqueduct from hot springs and arrived tepid. The town across the valley had the hot springs themselves. The town up the road had cold mountain water. It is told to buy white clothing, in a city famous for its glossy black wool. And ointment for its eyes, in a city known for making exactly that. Whoever wrote this knew the streets.",

        "The rest of the difficulty is that we are reading someone else's mail, in a code its first readers could follow and we often cannot. It borrows nearly everything from the Old Testament — hundreds of echoes of Daniel, Ezekiel and Isaiah, and not one direct quotation. On what the middle chapters describe, Christians have never agreed. Some read them as events under Rome in the first century. Some as a map of church history. Some as an end still ahead. The book supplies no timetable, and it is worth saying so plainly.",

        "Whatever is made of the middle, the ending is unmistakable. A new heaven and a new earth. And then the thing lost in Genesis 3 is handed back. God living with his people, face to face, no barrier and no exile. The word behind that living-with means to pitch a tent — the same word John used in his Gospel for the Word camping among us. Then the promise the book has been walking towards. *He will wipe away every tear from their eyes. Death will be no more.*",

        "The city is given measurements nobody could build. Twelve thousand stadia in every direction — a cube about fourteen hundred miles on a side. There is only one other cube in the Bible: the innermost room of the temple, the Most Holy Place, which one man entered on one day a year. The whole city is now that room. That is why John says he saw no temple in it. There is no sea either, which to a man exiled on an island is the thing that keeps you from everyone you love.",

        "The last two chapters put you back in a garden, with a river and a tree of life. Except it is a city now, full of people, with the nations bringing their glory in through gates that are never shut. Nothing is merely restored. The story does not end where it began. It ends further on. What it will not do is say when. It closes with an invitation to anyone who is thirsty, and a prayer: *Come, Lord Jesus.*",
      ],
      verseId: 'rev-21-4',
      takeaway: 'The story ends with God living with his people, and every tear wiped away.',
      questions: [
        {
          id: 'sq-new-1',
          question: 'Where was Revelation written from?',
          options: [
            'A prison cell in Rome',
            'Ephesus, the largest of the seven churches',
            'Patmos, a small island Rome used for banishment',
            'Jerusalem, during the Roman siege',
          ],
          answer: 2,
          explanation:
            'Thirteen square miles of rock in the Aegean. The circumstances matter to the reading: this is a book about empire, written by someone the empire had already dealt with.',
        },
        {
          id: 'sq-new-2',
          question: 'Why is "neither cold nor hot" a pointed thing to say to Laodicea in particular?',
          options: [
            'Its water arrived tepid by aqueduct, while its neighbours had hot springs and cold mountain water',
            'It stood between a desert and a mountain range and had no settled climate',
            'Half the city had burned and half had flooded in the same year',
            'Its congregation was split evenly between two rival teachers',
          ],
          answer: 0,
          explanation:
            'Every image in the message to Laodicea is drawn from the city itself — its water, its black wool, its eye ointment. The book is far more anchored to real places than its reputation suggests.',
        },
        {
          id: 'sq-new-3',
          question: 'The new Jerusalem is measured as a cube. What is the only other cube in the Bible?',
          options: [
            'Noah’s ark',
            'The courtyard of the tabernacle',
            'The stone rolled across the tomb',
            'The Most Holy Place in the temple',
          ],
          answer: 3,
          explanation:
            'One room, entered by one man on one day a year, has become the entire city everybody lives in. That is why John reports that there is no temple in it — there is no longer anywhere that is not one.',
        },
        {
          id: 'sq-new-4',
          question: 'How does the Bible’s final vision relate to the garden of Genesis?',
          options: [
            'It restores Eden exactly as it was',
            'It returns to a garden, with a river and a tree of life — but as a city full of people',
            'It abandons the earth entirely',
            'It leaves the ending deliberately open',
          ],
          answer: 1,
          explanation:
            'The river and the tree are back, inside city walls, with the nations carrying their glory through gates that never close. The end is not a reset to the beginning but the beginning brought to completion.',
        },
      ],
    },
  ],
};
